import { syntaxTree } from "@codemirror/language";
import {
  EditorSelection,
  EditorState,
  SelectionRange,
} from "@codemirror/state";
import { SyntaxNode, TreeCursor } from "@lezer/common";
import { EditorView } from "@uiw/react-codemirror";
import { omit } from "lodash-es";

import { SCHEMA_METADATA_KEYS, Schema } from "src/api/flowTypes";
import { isInternalSchemaDefKey } from "src/jsonSchema/validateJsonSchema";

const LINE_HEIGHT = 20;

/**
 * Finds the character range (start and end position) of a JSON value based on a dot-separated path.
 * It uses the CodeMirror 6 Lezer Syntax Tree for precise location finding.
 */
const findRangeFromPath = (state: EditorState, path: string) => {
  const tree = syntaxTree(state);
  if (!tree) return null;

  let currentNode: SyntaxNode | null = tree.topNode;
  const segments = path.split(".");

  // Check if the tree starts with a JsonText (Root) and immediately contains an Object
  if (currentNode.name === "JsonText") {
    currentNode = currentNode.firstChild;
  }

  if (!currentNode || currentNode.name !== "Object") return null;

  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    let found = false;

    // We expect the current node to be an 'Object' for key lookup
    if (!currentNode || currentNode.name !== "Object") return null;

    // Use a Cursor for efficient object traversal (Property -> PropertyName -> Value)
    const cursor: TreeCursor = currentNode.cursor();
    let currentKey = null;

    // Start scanning the children of the current Object node
    if (cursor.firstChild()) {
      do {
        // Look for a 'Property' node
        if (cursor.name === "Property") {
          const propertyNode = cursor.node;

          // Find the 'PropertyName' child
          const nameNode = propertyNode.getChild("PropertyName");
          if (nameNode) {
            // Get the text of the property name (including quotes)
            const nameText = state.doc.sliceString(nameNode.from, nameNode.to);

            // Remove surrounding quotes to compare with the path segment
            currentKey = nameText.replace(/"/g, "");

            if (currentKey === segment) {
              // Found the property key, now find its value
              // The value node is the sibling immediately after the PropertyName, or
              // the last child of the 'Property' node group that is a 'Value' type.

              // In the Lezer JSON grammar, the Value node is typically the next sibling after the ":" node
              const valueNode = propertyNode.lastChild;

              // Ensure we are only looking at the actual Value node (String, Array, Object, Number, etc.)
              if (
                valueNode &&
                valueNode.name.match(
                  /^(String|Array|Object|Number|Null|True|False)$/,
                )
              ) {
                if (i === segments.length - 1) {
                  // This is the final target value node
                  return { from: valueNode.from, to: valueNode.to };
                } else {
                  // This is an intermediate object/array, continue traversal
                  currentNode = valueNode;
                  found = true;
                  break;
                }
              }
            }
          }
        }
      } while (cursor.nextSibling());
    }

    if (!found && i < segments.length) {
      // Failed to find the segment, so the path is invalid for the current document structure
      return null;
    }
  }

  return null;
};

// Function to apply highlight and scroll
export const applyHighlightAndScroll = (view: EditorView, path: string) => {
  if (!view) return;
  const range = findRangeFromPath(view.state, path);
  let selection: SelectionRange | undefined = undefined;

  if (range) {
    // 1. Create a new decoration for the found range
    selection = EditorSelection.range(range.from, range.to);

    const lineNumber = view.state.doc.lineAt(selection.from).number;

    if (lineNumber > 0) {
      const scrollDom = view.scrollDOM;
      const clientHeight = scrollDom.clientHeight;
      const visibleLines = Math.floor(clientHeight / LINE_HEIGHT);
      const scrollTop = scrollDom.scrollTop;
      const lineTop = lineNumber * LINE_HEIGHT;
      const isVisible =
        lineTop >= scrollTop &&
        lineTop <= scrollTop + visibleLines * LINE_HEIGHT;

      if (!isVisible) {
        // padding 5 lines on top for better ux
        const scrollTopLineNumber = lineNumber - 6;
        view.scrollDOM.scrollTo({
          top: scrollTopLineNumber * LINE_HEIGHT,
          behavior: "smooth",
        });
      }
    }
  }

  view.dispatch({ selection });
};

// Ensures consistent ordering of keys in the stringified schema
const getReplacer = (_key: string, value: unknown): unknown => {
  const keys = [
    "$schema",
    ...SCHEMA_METADATA_KEYS,
    "type",
    "properties",
    "required",
    "sensitive",
    "order",
    "$defs",
    "definitions",
  ];

  return typeof value === "object" && !Array.isArray(value) && value !== null
    ? Object.keys(value)
        .sort((a, b) => keys.indexOf(a) - keys.indexOf(b))
        .reduce(
          (sorted, key) => {
            sorted[key] = value[key as keyof typeof value];
            return sorted;
          },
          {} as Record<string, unknown>,
        )
    : value;
};

export const stringifySchema = (schema: Schema | undefined): string => {
  if (!schema) return "";

  let keysToOmit: string[] = [];

  if ("$defs" in schema) {
    keysToOmit = Object.keys(schema["$defs"] ?? {})
      .filter((key) => isInternalSchemaDefKey(key))
      .map((key) => `$defs.${key}`);
  }

  if ("definitions" in schema) {
    keysToOmit = [
      ...keysToOmit,
      ...Object.keys(schema["definitions"] ?? {})
        .filter((key) => isInternalSchemaDefKey(key))
        .map((key) => `definitions.${key}`),
    ];
  }

  const cleanedSchema = omit(schema, keysToOmit);
  return JSON.stringify(cleanedSchema, getReplacer, 2);
};
